import React, { useState, useEffect } from "react";
import { RefreshCw, Check, X, Search, ChevronDown, ChevronUp } from "lucide-react";
import { FaBuilding, FaSwimmingPool, FaTableTennis, FaDumbbell, FaTheaterMasks, FaTree, FaCalendarAlt, FaCog } from "react-icons/fa";
import { API_BASE } from '../config'

async function apiFetch(path, opts = {}) {
  const token = sessionStorage.getItem("id_token");
  const resp = await fetch(`${API_BASE}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}`, ...opts.headers },
  });
  if (!resp.ok) throw new Error(`API ${resp.status}`);
  return resp.json();
}

// Icon lookup by icon key stored in DB
const ICON_MAP = {
  clubhouse: FaBuilding,
  swimming_pool: FaSwimmingPool,
  gym: FaDumbbell,
  party_hall: FaTheaterMasks,
  indoor_games: FaTableTennis,
  garden: FaTree,
};

function getIcon(iconKey) {
  return ICON_MAP[iconKey] || FaCog;
}

const TIME_SLOTS = {
  "06:00-08:00": "6 AM – 8 AM",
  "08:00-10:00": "8 AM – 10 AM",
  "10:00-12:00": "10 AM – 12 PM",
  "12:00-14:00": "12 PM – 2 PM",
  "14:00-16:00": "2 PM – 4 PM",
  "16:00-18:00": "4 PM – 6 PM",
  "18:00-20:00": "6 PM – 8 PM",
  "20:00-22:00": "8 PM – 10 PM",
};

const STATUS_BADGES = {
  pending: "bg-yellow-100 text-yellow-800",
  approved: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  cancelled: "bg-gray-100 text-gray-600",
};

function formatDate(val) {
  if (!val) return "";
  try {
    const d = new Date(val);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  } catch { return val; }
}

export default function AdminBookingManagement({ trialMaxPerMonth, isTrial }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState("pending");
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  const [toasts, setToasts] = useState([]);
  const [actionLoading, setActionLoading] = useState(null);
  const [amenities, setAmenities] = useState([]);

  const communityId = sessionStorage.getItem("community_id") || "";
  const reviewerEmail = (() => {
    try {
      const t = sessionStorage.getItem("id_token");
      if (!t) return "";
      const payload = JSON.parse(atob(t.split(".")[1]));
      return payload.email || "";
    } catch { return ""; }
  })();
  const reviewerName = (() => {
    try {
      const t = sessionStorage.getItem("id_token");
      if (!t) return "";
      const payload = JSON.parse(atob(t.split(".")[1]));
      return payload.name || payload.email?.split("@")[0] || "";
    } catch { return ""; }
  })();

  function showToast(message, type = "success") {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 3000);
  }

  async function loadBookings() {
    if (!communityId) return;
    setLoading(true);
    try {
      const data = await apiFetch(`/bookings?communityId=${communityId}`);
      const sorted = (data || []).sort((a, b) => (b.CREATED_AT || "").localeCompare(a.CREATED_AT || ""));
      setBookings(sorted);
    } catch (err) {
      console.error("loadBookings error:", err);
      showToast("Failed to load bookings", "error");
    } finally {
      setLoading(false);
    }
  }

  async function loadAmenities() {
    if (!communityId) return;
    try {
      const data = await apiFetch(`/amenities?communityId=${communityId}`);
      setAmenities(data || []);
    } catch (err) {
      console.error("loadAmenities error:", err);
    }
  }

  useEffect(() => { loadBookings(); loadAmenities(); }, [communityId]);

  async function handleAction(bookingId, status, reason) {
    setActionLoading(bookingId);
    try {
      const body = {
        COMMUNITY_ID: communityId,
        BOOKING_ID: bookingId,
        STATUS: status,
        REVIEWED_BY: reviewerEmail,
        REVIEWED_BY_NAME: reviewerName,
      };
      if (reason) body.REJECT_REASON = reason;
      await apiFetch("/bookings", { method: "PATCH", body: JSON.stringify(body) });
      showToast(`Booking ${status}`);
      setRejectId(null);
      setRejectReason("");
      loadBookings();
    } catch {
      showToast(`Failed to ${status} booking`, "error");
    } finally {
      setActionLoading(null);
    }
  }

  const statusCounts = {
    pending: bookings.filter((b) => b.STATUS === "pending").length,
    approved: bookings.filter((b) => b.STATUS === "approved").length,
    rejected: bookings.filter((b) => b.STATUS === "rejected").length,
  };

  const filtered = bookings.filter((b) => {
    const matchStatus = filterStatus === "all" || (b.STATUS || "") === filterStatus;
    const matchSearch = !searchTerm ||
      (b.BOOKED_BY_NAME || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (b.UNIT_NBR || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
      (b.AMENITY || "").toLowerCase().includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  return (
    <div className="p-4">
      {/* Toasts */}
      <div className="fixed top-4 right-4 z-[100] space-y-2">
        {toasts.map((t) => (
          <div key={t.id} className={`px-4 py-2 rounded-lg shadow-lg text-white text-sm ${t.type === "error" ? "bg-red-500" : "bg-green-500"}`}>
            {t.message}
          </div>
        ))}
      </div>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div>
          <h2 className="text-lg font-bold">Clubhouse Bookings</h2>
          <p className="text-xs">Approve or reject resident booking requests</p>
        </div>
        <button onClick={loadBookings} className="p-2 rounded-lg bg-gray-100 active:bg-gray-200">
          <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
        </button>
      </div>

      {/* Trial booking limit banner */}
      {isTrial !== false && trialMaxPerMonth != null && (
        <div style={{
          background: '#fffbeb',
          border: '1px solid #fbbf24',
          borderRadius: 8, padding: '8px 12px', fontSize: 12, marginBottom: 8,
          color: '#92400e'
        }}>
          {`Trial: residents are limited to ${trialMaxPerMonth} bookings/month. Upgrade for unlimited bookings.`}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-3 gap-2 mb-4">
        <div className="bg-yellow-50 border border-yellow-100 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-yellow-700">{statusCounts.pending}</p>
          <p className="text-[10px] text-yellow-600">Pending</p>
        </div>
        <div className="bg-green-50 border border-green-100 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-green-700">{statusCounts.approved}</p>
          <p className="text-[10px] text-green-600">Approved</p>
        </div>
        <div className="bg-red-50 border border-red-100 rounded-lg p-2 sm:p-3 text-center">
          <p className="text-lg sm:text-xl font-bold text-red-700">{statusCounts.rejected}</p>
          <p className="text-[10px] text-red-600">Rejected</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-3">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, unit, amenity..."
          className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm"
        />
      </div>

      {/* Filter pills */}
      <div className="filter-pills mb-4 pb-2">
        {["all", "pending", "approved", "rejected"].map((s) => (
          <button
            key={s}
            onClick={() => setFilterStatus(s)}
            className={`filter-pill px-4 py-2 rounded-lg text-sm ${
              filterStatus === s ? "bg-blue-600 text-white" : "bg-gray-100 text-gray-700"
            }`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
            {s !== "all" && statusCounts[s] > 0 ? ` (${statusCounts[s]})` : ""}
          </button>
        ))}
      </div>

      {/* Booking List */}
      <div className="space-y-3">
        {loading && <div className="text-center py-8 text-gray-400">Loading...</div>}
        {!loading && filtered.length === 0 && (
          <div className="text-center py-8">
            <FaBuilding size={28} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm">No bookings found</p>
          </div>
        )}

        {!loading &&
          filtered.map((b) => {
            const amenityData = amenities.find((a) => a.AMENITY_ID === b.AMENITY || a.ICON === b.AMENITY);
            const amenity = amenityData
              ? { label: amenityData.NAME, icon: getIcon(amenityData.ICON) }
              : { label: b.AMENITY, icon: FaBuilding };
            const AmenityIcon = amenity.icon;
            const status = b.STATUS || "pending";
            const isExpanded = expandedId === b.BOOKING_ID;

            return (
              <div key={b.BOOKING_ID} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <button
                  onClick={() => setExpandedId(isExpanded ? null : b.BOOKING_ID)}
                  className="w-full text-left p-4"
                >
                  <div className="flex items-start gap-3">
                    <div className={`p-2 rounded-lg flex-shrink-0 ${
                      status === "approved" ? "bg-green-100 text-green-700" :
                      status === "rejected" ? "bg-red-100 text-red-700" :
                      status === "pending" ? "bg-yellow-100 text-yellow-700" :
                      "bg-gray-100 text-gray-500"
                    }`}>
                      <AmenityIcon size={20} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <h3 className="font-medium text-gray-900 truncate">{amenity.label}</h3>
                        <div className="flex items-center gap-2">
                          <span className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGES[status]}`}>
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                          </span>
                          {isExpanded ? <ChevronUp size={16} className="text-gray-400" /> : <ChevronDown size={16} className="text-gray-400" />}
                        </div>
                      </div>
                      <p className="text-sm text-gray-600 mt-0.5">
                        Unit {b.UNIT_NBR} • {b.BOOKED_BY_NAME || b.BOOKED_BY}
                      </p>
                      <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-gray-500">
                        <span className="flex items-center gap-1">
                          <FaCalendarAlt size={10} /> {formatDate(b.BOOKING_DATE)}
                        </span>
                        <span>{TIME_SLOTS[b.SLOT] || b.SLOT}</span>
                      </div>
                    </div>
                  </div>
                </button>

                {isExpanded && (
                  <div className="border-t border-gray-100 px-4 pb-4">
                    <div className="grid grid-cols-2 gap-3 mt-3 text-xs">
                      <div>
                        <p className="text-gray-500">Booked By</p>
                        <p className="font-medium">{b.BOOKED_BY_NAME || b.BOOKED_BY}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Unit</p>
                        <p className="font-medium">{b.UNIT_NBR}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Date</p>
                        <p className="font-medium">{formatDate(b.BOOKING_DATE)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Time Slot</p>
                        <p className="font-medium">{TIME_SLOTS[b.SLOT] || b.SLOT}</p>
                      </div>
                      {b.PURPOSE && (
                        <div className="col-span-2">
                          <p className="text-gray-500">Purpose</p>
                          <p className="font-medium">{b.PURPOSE}</p>
                        </div>
                      )}
                      <div>
                        <p className="text-gray-500">Requested</p>
                        <p className="font-medium">{formatDate(b.CREATED_AT)}</p>
                      </div>
                      {b.REVIEWED_BY_NAME && (
                        <div>
                          <p className="text-gray-500">Reviewed By</p>
                          <p className="font-medium">{b.REVIEWED_BY_NAME}</p>
                        </div>
                      )}
                      {b.REJECT_REASON && (
                        <div className="col-span-2">
                          <p className="text-gray-500">Rejection Reason</p>
                          <p className="font-medium text-red-600">{b.REJECT_REASON}</p>
                        </div>
                      )}
                    </div>

                    {/* Action Buttons — only for pending */}
                    {status === "pending" && (
                      <div className="mt-4">
                        {rejectId === b.BOOKING_ID ? (
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="Reason for rejection (optional)"
                              value={rejectReason}
                              onChange={(e) => setRejectReason(e.target.value)}
                              className="w-full p-2 border border-gray-300 rounded-lg text-sm"
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={() => { setRejectId(null); setRejectReason(""); }}
                                className="flex-1 px-3 py-2 text-sm text-gray-600 bg-gray-100 rounded-lg"
                              >
                                Cancel
                              </button>
                              <button
                                onClick={() => handleAction(b.BOOKING_ID, "rejected", rejectReason)}
                                disabled={actionLoading === b.BOOKING_ID}
                                className="flex-1 px-3 py-2 text-sm text-white bg-red-600 rounded-lg disabled:opacity-50"
                              >
                                {actionLoading === b.BOOKING_ID ? "..." : "Confirm Reject"}
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleAction(b.BOOKING_ID, "approved")}
                              disabled={actionLoading === b.BOOKING_ID}
                              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-white bg-green-600 rounded-lg active:bg-green-700 disabled:opacity-50"
                            >
                              <Check size={16} /> Approve
                            </button>
                            <button
                              onClick={() => setRejectId(b.BOOKING_ID)}
                              className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-white bg-red-600 rounded-lg active:bg-red-700"
                            >
                              <X size={16} /> Reject
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
      </div>
    </div>
  );
}
